import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import {
  HostedFrame,
  type HostedFrameMessageEvent,
  ModalDialogue,
  theme,
  useNoticeBannersStackActions,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useSendLogoutToAuthMachine } from '../../../../../../../context/AuthMachine';
import { getAzureAuthEmbeddedFrameLogoutUrl } from '../../../../../../../context/AzureAuth/helpers/get-azure-auth-embedded-frame-url';
import { removeTokens } from '../../../../../../../context/AzureAuth/helpers/remove-tokens';
import { useCustomer } from '../../../../../../../context/Customer';
import { LoadingAnimation } from '../../../../../../LoadingAnimation';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenuSignOutItemEmbeddedModal = (props: AccountMenuSignOutItemEmbeddedProps) => {
  const { push: addBanner } = useNoticeBannersStackActions();
  const { track } = useTelemetry();
  const { signOut } = useCustomer();
  const sendLogoutAction = useSendLogoutToAuthMachine();

  const { isVisible, closeModal, resetAppState } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isSigningOutRef = useRef(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const closeModalWithError = useCallback(() => {
    closeModal();
    addBanner({
      palette: 'caution',
      text: 'Logging out failed. Please try again.',
    });
  }, [addBanner, closeModal]);

  const closeModalAndSignOut = useCallback(async () => {
    if (isSigningOutRef.current) {
      return;
    }

    isSigningOutRef.current = true;

    try {
      const hasSignedOut = await signOut();

      if (!hasSignedOut) {
        closeModalWithError();

        return;
      }

      sendLogoutAction();
      await removeTokens();
      resetAppState();
      closeModal();
    } finally {
      isSigningOutRef.current = false;
    }
  }, [closeModal, closeModalWithError, resetAppState, sendLogoutAction, signOut]);

  const onMessage = useCallback(
    (event: HostedFrameMessageEvent) => {
      const data = JSON.parse(event?.nativeEvent?.data ?? '{}');
      const eventType = data?.type;

      if (!eventType) return;

      switch (eventType) {
        case 'LOGOUT_SUCCESS': {
          clearTimeout(logoutTimeoutRef.current);
          void closeModalAndSignOut();
          track('azure-ad.logout.success');

          break;
        }

        case 'LOGOUT_ERROR': {
          clearTimeout(logoutTimeoutRef.current);
          closeModalWithError();
          track('azure-ad.logout.failure');

          break;
        }
      }
    },
    [closeModalAndSignOut, track, closeModalWithError],
  );

  const signOutOnLogoutTimeout = useCallback(() => {
    void closeModalAndSignOut();
    track('azure-ad.logout.failure', { systemError: 'Sign-out frame timed out' });
  }, [closeModalAndSignOut, track]);

  const startLogoutTimeout = useCallback(() => {
    if (!isVisible) {
      return;
    }

    logoutTimeoutRef.current = setTimeout(signOutOnLogoutTimeout, LOGOUT_TIMEOUT_MS);

    return () => {
      clearTimeout(logoutTimeoutRef.current);
    };
  }, [isVisible, signOutOnLogoutTimeout]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(startLogoutTimeout, [startLogoutTimeout]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue size="small" visible={isVisible}>
      <SafeAreaView style={styles.container} edges={SAFE_AREA_EDGES}>
        <LoadingAnimation size="small" />
      </SafeAreaView>

      {isVisible ? (
        <HostedFrame
          frameId="azure-auth-sign-out-frame"
          frameHeight={0}
          source={AUTH_FRAME_SRC}
          onMessage={onMessage}
        />
      ) : null}
    </ModalDialogue>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOGOUT_TIMEOUT_MS = 15_000;

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AUTH_FRAME_SRC = {
  uri: getAzureAuthEmbeddedFrameLogoutUrl(),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type AccountMenuSignOutItemEmbeddedProps = {
  isVisible: boolean;
  closeModal: () => void;
  resetAppState: () => void;
};
